<template>
  <div class="orcamentos-form">
    <!-- Alert for date filter warning -->
    <inline-message v-if="showDateAlert" severity="warn" :closable="false">
      <template #default>
        <strong>{{ t('Atenção') }}</strong>
        <p>{{ t('O filtro configurado não contempla a data atual.') }}</p>
        <p>{{ t('Isso significa que o orçamento criado não irá aparecer na listagem porque ele terá a data de hoje.') }}</p>
        <p>{{ t('Para que ele apareça, configure o filtro para abranger o dia de hoje.') }}</p>
      </template>
    </inline-message>

    <!-- Cancel/Reactivate buttons for existing budgets -->
    <div v-if="isEditMode && orcamentoData.orcamento_id > 0" class="action-buttons-right">
      <Button
        v-if="orcamentoData.orcamento_status !== 'Cancelado'"
        data-testid="orcamento-cancel-btn"
        @click="$emit('cancel-orcamento', orcamentoData.orcamento_id)"
        :label="t('Cancelar orçamento')"
        icon="pi pi-ban"
        severity="danger"
      />
      <Button
        v-if="hasFullPermission"
        data-testid="orcamento-delete-btn"
        @click="$emit('delete-orcamento', orcamentoData.orcamento_id)"
        :label="t('Excluir orçamento')"
        icon="pi pi-trash"
        severity="danger"
      />
    </div>

    <!-- Cancelled budget warning -->
    <inline-message v-if="isEditMode && orcamentoData.orcamento_status === 'Cancelado'" severity="warn" :closable="false" :pt="{ transition: { name: '', appear: false } }">
      <template #default>
        <div class="cancelled-budget-message">
          <h3><i class="pi pi-exclamation-triangle"></i> {{ t('Orçamento cancelado') }}</h3>
          <p>{{ t('Para editar, reative o orçamento') }}</p>
          <Button
            data-testid="orcamento-reactivate-btn"
            :label="t('Reativar orçamento')"
            severity="secondary"
            @click="$emit('reactivate-orcamento', orcamentoData.orcamento_id)"
          />
        </div>
      </template>
    </inline-message>

    <!-- Download PDF button -->
    <Button
      v-if="isEditMode && orcamentoData.orcamento_id > 0"
      data-testid="orcamento-download-pdf-btn"
      @click="$emit('download-pdf', orcamentoData.orcamento_id)"
      :label="t('Download orçamento PDF')"
      icon="pi pi-file-pdf"
      severity="primary"
      class="pdf-download-button"
    />

    <!-- General Information Section -->
    <box type="primary" :title="t('Informações gerais')" elem-id="boxInformacoesGeraisOrcamento">
      <franchise-select 
        elem-id="orcamento_select_franquias"
        :label="t('Franquia')"
        v-model="orcamentoData.orcamento_franquia_id"
        :callback="handleFranquiaChange"
      />

      <select-list
        v-if="hasFullPermission"
        data-testid="orcamento-atendente-select"
        elem-id="orcamento_select_atendente"
        :label="t('Atendente')"
        :itens-select="atendentes"
        :auto-select-first="false"
        v-model="orcamentoData.orcamento_user_id"
        :placeholder="t('Quem atendeu o orçamento')"
      />

      <select-list
        data-testid="orcamento-origem-select"
        :label="t('Origem')"
        elem-id="orcamento_select_origens"
        :itens-select="origens"
        v-model="orcamentoData.orcamento_origem"
        :placeholder="t('Escolha a origem do orçamento')"
      />

      <div v-if="isEditMode && orcamentoData.orcamento_id > 0">
        <select-list
          data-testid="orcamento-status-select"
          :label="t('Status do orçamento')"
          elem-id="orcamento_select_status"
          v-model="orcamentoData.orcamento_status"
          :itens-select="statusOptions"
        />
      </div>
    </box>

    <!-- Client Information Section -->
    <box 
      type="primary" 
      :title="t('Cliente')" 
      elem-id="boxClienteOrcamento"
      :validation="() => validateClientSection">
      <input-text
        data-testid="orcamento-cliente-nome-input"
        elem-id="nomeCliente"
        :validation="() => isClientNameValid"
        :ng-required="true"
        v-model="clienteData.cliente_nome"
        :label="t('Nome do cliente')"
        :placeholder="t('Nome do cliente')"
      />

      <!-- Documento dinâmico baseado no país da franquia -->
      <input-document
        data-testid="orcamento-cliente-documento-input"
        elem-id="documentoCliente"
        v-model="clienteData.cliente_cpf"
        :document-type="selectedDocumentType"
        :label="t(currentDocumentPresentation.label)"
        @validation="handleDocumentValidation">
      </input-document>

      <input-phone
        data-testid="orcamento-cliente-telefone-input"
        :label="t('Telefone')"
        elem-id="telefoneCliente"
        v-model="phoneModel"
        :default-country="defaultCountry"
        :language="userLanguage"
      />
    </box>

    <!-- Address Section -->
    <box :title="t('Endereço')" type="primary" elem-id="boxEnderecoOrcamento">
      <div class="address-row">
        <input-text
          v-if="isBrazil"
          data-testid="orcamento-cliente-cep-input"
          elem-id="cepCliente"
          v-model="clienteData.cliente_cep"
          :label="t('CEP')"
          :placeholder="t('CEP')"
          @blur="handleCepLookup"
          class="address-cep"
        />
        <input-text
          data-testid="orcamento-cliente-rua-input"
          elem-id="ruaCliente"
          :validation="() => isRuaValid"
          :required="enderecoObrigatorio"
          v-model="clienteData.cliente_rua"
          :label="t('Rua')"
          :placeholder="t('Rua')"
          :class="isBrazil ? 'address-street' : 'address-street-wide'"
        />
        <input-text
          data-testid="orcamento-cliente-numero-input"
          elem-id="numeroEnderecoCliente"
          :validation="() => isNumeroValid"
          :required="enderecoObrigatorio"
          v-model="clienteData.cliente_numero"
          :label="t('Número')"
          :placeholder="t('Número')"
          class="address-number"
        />
      </div>

      <input-text
        v-if="!isSpanish"
        data-testid="orcamento-cliente-complemento-input"
        elem-id="complementoCliente"
        v-model="clienteData.cliente_complemento"
        :label="t('Complemento')"
        :placeholder="t('Complemento')"
      />

      <input-text
        data-testid="orcamento-cliente-bairro-input"
        elem-id="bairroCliente"
        v-model="clienteData.cliente_bairro"
        :label="t('Bairro')"
        :placeholder="t('Bairro')"
      />

      <location-select
        :label="t('Localidade')"
        :placeholder="t('Clique para escolher')"
        elem-id="orcamento_select_location"
        v-model:location-id="orcamentoData.orcamento_location_id"
        v-model:location-type="orcamentoData.orcamento_location_type"
        :franchise-id="orcamentoData.orcamento_franquia_id"
        :is-ready="locationSelectReady"
      />
    </box>

    <!-- Services Section (only for new budgets) -->
    <box
      v-if="!isEditMode || orcamentoData.orcamento_id == 0"
      type="primary"
      :title="t('Serviços')"
      elem-id="boxServicosOrcamento">
      <!-- AI Completion (Version 1 users only) -->
      <object-completion
        v-if="isVersion1User"
        elem-id="orcamentoCompletionComponent"
        :label="t('Completar com IA')"
        v-model="servicosCompletionData"
        :get-json-schema-to-fill="getJsonSchemaForServices"
        :extra-informations-object="getExtraInformations"
        :success-callback="handleAiSuccess"
        :error-callback="handleAiError"
        api-endpoint="/index.php/completions/completions/infer"
        :placeholder="t('Digite o conteúdo para completar')"
        :description="t('completar-com-ia-descricao')"
        debug="false"
        :cancel-label="t('Cancelar')"
        :send-label="t('Enviar')"
        :ng-required="true"
      />

      <div class="add-service-container">
        <Button
          id="btnServicos"
          data-testid="orcamento-form-add-service-btn"
          :label="t('Adicionar serviço')"
          icon="pi pi-plus"
          severity="primary"
          size="small"
          @click="openAddServicoWizard"
        />
      </div>

      <!-- Services List -->
      <div v-if="servicos.length > 0" class="services-list">
        <div
          v-for="(itemServico, index) in servicos"
          :key="itemServico.uuid"
          class="service-item">
          <Badge :value="(index + 1).toString()" severity="secondary" class="service-badge" />
          <div class="service-info">
            <div class="service-categoria">{{ itemServico.categoria_nome }}</div>
            <div class="service-name">{{ getServicoName(itemServico) }}</div>
            <div class="service-details">
              <span class="service-qty">{{ itemServico.qtd }}x</span>
            </div>
          </div>
          <div class="service-total">{{ n(parseFloat(String(itemServico.valor)) || 0, 'currency') }}</div>
          <Button
            icon="pi pi-times"
            severity="danger"
            text
            rounded
            size="small"
            :data-testid="`orcamento-remove-service-${index}-btn`"
            @click="removerServico(index)"
            :title="t('Remover')"
            class="service-remove"
          />
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="services-empty">
        <i class="pi pi-inbox"></i>
        <span>{{ t('Nenhum serviço adicionado') }}</span>
      </div>
    </box>

    <!-- Observations Section -->
    <box type="primary" :title="t('Observações do orçamento')" elem-id="containerObservacoesOrcamento">
      <occurrences-page
        owner="orcamento"
        :owner-id="orcamentoData.orcamento_id"
        :mode="orcamentoData.orcamento_id > 0 ? 'modal' : 'inline'"
        :show-inline-save-btn="false"
        v-model="orcamentoData.orcamento_observacao"
        :allow-delete="true"
        :add-label="t('Adicionar observação')"
        :modal-title="t('Nova observação')"
        :description-label="t('Descrição')"
        :close-label="t('Fechar')"
        :save-label="t('Salvar observação')"
        :datatable-language-url="datatableLanguageUrl"
      />
    </box>

    <!-- Total (for new budgets only) -->
    <h3 v-if="!isEditMode || orcamentoData.orcamento_id === 0" class="total-value">
      {{ t('Total') }}: {{ n(totalValue, 'currency') }}
    </h3>

    <!-- OrcamentoAddServicoWizard is centralized in ModalManager.vue and opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
// OrcamentoAddServicoWizard is centralized in ModalManager.vue - opened via eventBus
import { useI18n } from 'vue-i18n';
import { useOrcamento } from '../../composables/useOrcamento';
import { useCliente } from '../../composables/useCliente';
import { permissionsState, permissionsService } from '../../services/permissionsService';
import { config } from '../../services/configService';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { phoneFromFields, phoneToFields } from '../../composables/usePhoneFormat';
import { useCustomerInternationalConfig } from '../../composables/useCustomerInternationalConfig';
import type { Orcamentos_SalvarOrcamentoResponse } from '../../contracts/Orcamentos_SalvarOrcamentoResponse';
import type { Orcamentos_SalvarOrcamentoInput } from '../../contracts/Orcamentos_SalvarOrcamentoInput';
import type { Orcamentos_UpdateOrcamentoInput } from '../../contracts/Orcamentos_UpdateOrcamentoInput';
import type { OrcamentoClienteInputRow } from '../../contracts/OrcamentoClienteInputRow';
import type { OrcamentoDataInputRow } from '../../contracts/OrcamentoDataInputRow';
import type { OrcamentoServicoInputRow } from '../../contracts/OrcamentoServicoInputRow';
import type { OrcamentoUpdateClienteInputRow } from '../../contracts/OrcamentoUpdateClienteInputRow';
import type { OrcamentoUpdateDataInputRow } from '../../contracts/OrcamentoUpdateDataInputRow';
import type { OrcamentosServicos_SelectserviciosRow } from '../../contracts/OrcamentosServicos_SelectserviciosRow';

type Id = number | string;

interface OrcamentoData {
  orcamento_id: number;
  orcamento_franquia_id: Id;
  orcamento_user_id: string;
  orcamento_location_id: Id;
  orcamento_location_type: string;
  orcamento_valor: number;
  orcamento_origem: Id;
  orcamento_data: string;
  orcamento_status: string;
  orcamento_observacao: string;
  orcamento_cliente_id: Id;
  [key: string]: unknown;
}

interface ClienteData {
  cliente_id: Id;
  cliente_nome: string;
  cliente_cpf: string;
  cliente_document_type: string;
  cliente_ddi: string;
  cliente_ddd: string;
  cliente_telefone: string;
  cliente_rua: string;
  cliente_numero: string;
  cliente_complemento: string;
  cliente_bairro: string;
  cliente_cep: string;
  cliente_location_id: Id;
  cliente_location_type: string;
  cliente_franquia_id: Id;
  [key: string]: unknown;
}

interface PhoneModel {
  phoneNumber: string;
  countryCode: string;
  areaCode: string;
}

interface ServicoItem {
  servico_id: Id;
  valor: string | number;
  qtd: string | number;
  categoria_id: Id;
  categoria_nome: string;
  uuid: string;
  [key: string]: unknown;
}

// Service-row shape comes from the OrcamentosServicos_SelectserviciosRow
// contract (source of truth). No local duplicate — see fetchServicos() in
// useOrcamento.ts wired to responseContract: OrcamentosServicos_SelectserviciosResponse.
type ServicoOption = OrcamentosServicos_SelectserviciosRow;

interface Categoria {
  servicoscategoria_id: Id;
  servicoscategoria_nome: string;
  servicos: ServicoOption[];
}

interface CategoriasMap {
  [key: string]: Categoria;
}

interface SelectOption {
  ID: Id;
  Name: string;
}

interface DocumentValidationResult {
  valid: boolean;
  error?: string;
}

interface CepResult {
  logradouro?: string;
  bairro?: string;
  localidade?: string;
}

/**
 * Result of a save (create/update) call. Aligned with the
 * Orcamentos_SalvarOrcamentoResponse contract envelope. `message` is added as
 * an optional UI-only field for compatibility — the backend currently returns
 * only the success/dados/error envelope, so the success-toast falls back to
 * the i18n default when absent.
 */
type SaveResult = Orcamentos_SalvarOrcamentoResponse & {
  message?: string;
};

// Props
const props = withDefaults(
  defineProps<{
    isEditMode?: boolean;
  }>(),
  {
    isEditMode: false,
  }
);

// Emits
const emit = defineEmits<{
  (e: 'save-success', result: SaveResult): void;
  (e: 'validation-error'): void;
  (e: 'cancel-orcamento', id: Id): void;
  (e: 'delete-orcamento', id: Id): void;
  (e: 'reactivate-orcamento', id: Id): void;
  (e: 'download-pdf', id: Id): void;
  (e: 'load-client-data'): void;
}>();

// Composables
const { t, n } = useI18n();
const {
  fetchOrcamento,
  createOrcamento,
  updateOrcamento,
  fetchServicos,
  fetchOrigens,
  consultaCep
} = useOrcamento();
const { fetchCliente, fetchDocumentConfig } = useCliente();

// Reactive data
const orcamentoData = ref<OrcamentoData>({
  orcamento_id: 0,
  orcamento_franquia_id: 0,
  orcamento_user_id: '',
  orcamento_location_id: '',
  orcamento_location_type: '',
  orcamento_valor: 0,
  orcamento_origem: 0,
  orcamento_data: '',
  orcamento_status: 'Pendente',
  orcamento_observacao: '',
  orcamento_cliente_id: 0
});

const clienteData = ref<ClienteData>({
  cliente_id: 0,
  cliente_nome: '',
  cliente_cpf: '',
  cliente_document_type: '',
  cliente_ddi: '',
  cliente_ddd: '',
  cliente_telefone: '',
  cliente_rua: '',
  cliente_numero: '',
  cliente_complemento: '',
  cliente_bairro: '',
  cliente_cep: '',
  cliente_location_id: 0,
  cliente_location_type: '',
  cliente_franquia_id: 0
});

const phoneModel = ref<PhoneModel>({
  phoneNumber: '',
  countryCode: '',
  areaCode: ''
});

log('OrcamentosForm', 'setup', 'phoneModel initialized:', phoneModel.value);

const servicos = ref<ServicoItem[]>([]);
const categorias = ref<CategoriasMap>({});
const atendentes = ref<SelectOption[]>([]);
const origens = ref<SelectOption[]>([]);
// Removed: locationEscolhida intermediary state
const enviandoDados = ref<boolean>(false);
const servicosCompletionData = ref<string>('');
const isLoadingData = ref<boolean>(false);
const locationSelectReady = ref<boolean>(false);
// addServicoWizardRef removed - wizard is now centralized in ModalManager.vue

// Computed properties
const statusOptions = computed<SelectOption[]>(() => {
  return [
    { ID: 'Pendente', Name: t('Pendente') },
    { ID: 'Respondido', Name: t('Respondido') },
    { ID: 'Aprovado', Name: t('Aprovado') },
    { ID: 'Parcialmente concluído', Name: t('Parcialmente concluido') },
    { ID: 'Todos os serviços realizados', Name: t('Todos os serviços realizados') },
    { ID: 'Cancelado', Name: t('Cancelado') }
  ];
});

const hasFullPermission = computed<boolean>(() => {
  return permissionsState.user?.groups?.includes('Full') || false;
});

const isVersion1User = computed<boolean>(() => {
  return Number(permissionsState.user?.versaoSistema) === 1;
});

const userLanguage = computed<string>(() => {
  return permissionsState.user?.language || 'PT';
});

// Country configuration for international document support
const {
  config: countryConfig,
  currentCountry,
  setCountryFromName,
  setDocumentConfig,
  selectedDocumentType,
  currentDocumentPresentation,
  phoneCountryCode
} = useCustomerInternationalConfig();

// Re-keyed off currentCountry (ISO) instead of comparing the raw franquia
// name — tolerant of both franquias.pais storage states (S1b).
const isBrazil = computed<boolean>(() => currentCountry.value === 'BR');

// Watch franchise changes to update country config and fetch document config
watch(() => orcamentoData.value.orcamento_franquia_id, async (franquiaId) => {
  if (franquiaId && permissionsState.franquias && permissionsState.franquias[franquiaId as keyof typeof permissionsState.franquias]) {
    const franquia = permissionsState.franquias[franquiaId as keyof typeof permissionsState.franquias] as { Dados?: { Pais?: string } };
    const pais = franquia.Dados?.Pais;
    if (pais) {
      setCountryFromName(pais);
      log('OrcamentosForm', 'watch:franquia_id', 'Country set to:', pais);
    }

    // Fetch document config from backend (cached)
    try {
      const docConfig = await fetchDocumentConfig(Number(franquiaId));
      setDocumentConfig(docConfig);
      clienteData.value.cliente_document_type = selectedDocumentType.value;
      log('OrcamentosForm', 'watch:franquia_id', 'Document config loaded:', docConfig);
    } catch (err) {
      log('OrcamentosForm', 'watch:franquia_id', 'Error fetching document config', err);
    }
  }
}, { immediate: true });

// Document validation state
const documentValidationError = ref<string>('');

// Handle document validation result from InputDocument
const handleDocumentValidation = (result: DocumentValidationResult): void => {
  log('OrcamentosForm', 'handleDocumentValidation', 'Validation result:', result);
  documentValidationError.value = result.valid ? '' : (result.error || '');
};

const isSpanish = computed<boolean>(() => {
  return userLanguage.value === 'ES';
});

// Driven by currentCountry (set from the franchise's country via the watch
// above) instead of a hand-rolled name map — phoneCountryCode already covers
// all 10 supported countries and both franquias.pais storage states (S1b).
const defaultCountry = computed<string>(() => phoneCountryCode.value);

const showDateAlert = computed<boolean>(() => {
  // Check if current date is within filter range using useFiltro composable
  // This will be handled by the parent component or through event bus
  // For now, we'll disable this check as it should be handled at a higher level
  return false;
});

const datatableLanguageUrl = computed<string>(() => {
  // Get from translations service if available
  return '';
});

const totalValue = computed<number>(() => {
  // valor is the line total: the quantity does not multiply it.
  return servicos.value.reduce((sum, servico) => sum + (parseFloat(String(servico.valor)) || 0), 0);
});

// Currency symbol from i18n number format
const currencySymbol = computed<string>(() => {
  try {
    const formatted = n(0, 'currency');
    const symbol = String(formatted).replace(/[0-9\s.,]/g, '').trim();
    return symbol || '€';
  } catch (e) {
    return 'R$';
  }
});

// Validation
const isClientNameValid = computed<boolean>(() => {
  return !!clienteData.value.cliente_nome && clienteData.value.cliente_nome.trim() !== '';
});

const isRuaValid = computed<boolean>(() => {
  return !!clienteData.value.cliente_rua && clienteData.value.cliente_rua.trim() !== '';
});

const isNumeroValid = computed<boolean>(() => {
  return !!clienteData.value.cliente_numero && clienteData.value.cliente_numero.trim() !== '';
});

// O que falta no cadastro para o orçamento ir à agenda (mesmos códigos da regra AgendaDoOrcamento
// do backend, chamado DS 2026-0068). A zona do orçamento vai para o cliente ao salvar.
const faltaParaAgendar = computed<string[]>(() => {
  const falta: string[] = [];
  if (!isRuaValid.value) falta.push('sem_rua');
  if (!isNumeroValid.value) falta.push('sem_numero');
  if (!Number(orcamentoData.value.orcamento_location_id) && !Number(clienteData.value.cliente_location_id)) falta.push('sem_zona');
  return falta;
});

// Rua, número e zona obrigatórios para salvar quando o tenant desliga 'clientesemendereco'
// (DS 2026-0068). A zona só é cobrada da franquia que tem localidades para escolher.
const enderecoObrigatorio = !config.isFeatureEnabled('clientesemendereco');
const faltaNoCadastro = computed<string[]>(() => {
  if (!enderecoObrigatorio) return [];
  const franquiaTemZonas = permissionsService.getLocationsByID(Number(orcamentoData.value.orcamento_franquia_id)).length > 0;
  return faltaParaAgendar.value.filter((motivo) => motivo !== 'sem_zona' || franquiaTemZonas);
});

const validateClientSection = computed<string[]>(() => {
  const errors: string[] = [];
  if (!isClientNameValid.value) {
    errors.push(t('Você precisa preencher o nome'));
  }
  if (faltaNoCadastro.value.includes('sem_rua')) errors.push(t('Você precisa preencher a rua'));
  if (faltaNoCadastro.value.includes('sem_numero')) errors.push(t('Você precisa preencher o número'));
  if (faltaNoCadastro.value.includes('sem_zona')) errors.push(t('Você precisa escolher a localidade'));
  return errors;
});

const canSave = computed<boolean>(() => {
  // Basic validation
  if (!isClientNameValid.value) return false;
  if (!orcamentoData.value.orcamento_franquia_id) return false;
  if (!orcamentoData.value.orcamento_status) return false;
  if (faltaNoCadastro.value.length > 0) return false;

  // For new budgets, at least one service is required
  if ((!props.isEditMode || orcamentoData.value.orcamento_id === 0) && servicos.value.length === 0) {
    return false;
  }

  // Not currently sending
  if (enviandoDados.value) return false;

  return true;
});

// Methods
const initializeForNew = async (): Promise<void> => {
  log('OrcamentosForm', 'initializeForNew', 'Initializing for new budget');

  resetForm();

  try {
    // Load initial data
    await Promise.all([
      loadOrigens(),
      loadServicos()
    ]);

    if (origens.value.length > 0) {
      orcamentoData.value.orcamento_origem = origens.value[0].ID;
    }

    // Tell LocationSelect we're ready to listen for franchise changes
    locationSelectReady.value = true;
    log('OrcamentosForm', 'initializeForNew', 'LocationSelect ready signal sent');

    log('OrcamentosForm', 'initializeForNew', 'Initialized successfully');
    eventBus.emit('orcamento.form.ready', { mode: 'new' });
  } catch (error) {
    log('OrcamentosForm', 'initializeForNew', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro-ao-inicializar-formulario'));
  }
};

const loadForEdit = async (orcamentoId: Id): Promise<void> => {
  log('OrcamentosForm', 'loadForEdit', 'Loading budget for edit', orcamentoId);

  // Set loading flag to prevent watchers from auto-selecting
  isLoadingData.value = true;
  let loadSuccess = false;

  try {
    const response = await fetchOrcamento(Number(orcamentoId)) as { dados?: { orcamento?: Partial<OrcamentoData>; cliente?: Partial<ClienteData> } };

    log('OrcamentosForm', 'loadForEdit', 'Received response from fetchOrcamento:', response);
    log('OrcamentosForm', 'loadForEdit', 'response.dados:', response.dados);

    // Extract data from response.dados
    const orcamentoDataFromAPI = response.dados?.orcamento || response.dados || {};
    const clienteDataFromAPI = response.dados?.cliente || {};

    log('OrcamentosForm', 'loadForEdit', 'orcamentoDataFromAPI:', orcamentoDataFromAPI);
    log('OrcamentosForm', 'loadForEdit', 'clienteDataFromAPI:', clienteDataFromAPI);

    // Map orcamento data
    Object.assign(orcamentoData.value, orcamentoDataFromAPI);

    // Map cliente data
    if (clienteDataFromAPI && Object.keys(clienteDataFromAPI).length > 0) {
      log('OrcamentosForm', 'loadForEdit', 'Mapping cliente data:', clienteDataFromAPI);
      Object.assign(clienteData.value, clienteDataFromAPI);
    } else {
      log('OrcamentosForm', 'loadForEdit', 'WARNING: No cliente data in response!');
    }
    
    // Set phone model using usePhoneFormat composable
    log('OrcamentosForm', 'loadOrcamento', 'Setting phoneModel from cliente data:', {
      cliente_telefone: clienteData.value.cliente_telefone,
      cliente_ddi: clienteData.value.cliente_ddi,
      cliente_ddd: clienteData.value.cliente_ddd
    });

    phoneModel.value = phoneFromFields({
      ddi: clienteData.value.cliente_ddi,
      ddd: clienteData.value.cliente_ddd,
      telefone: clienteData.value.cliente_telefone
    });

    log('OrcamentosForm', 'loadOrcamento', 'phoneModel set to:', phoneModel.value);
    
    // Preserve existing location fields on orcamento/cliente (no intermediary state required)
    
    // Load dependent data
    await Promise.all([
      loadOrigens(),
      loadAtendentes(orcamentoData.value.orcamento_franquia_id),
      loadServicos()
    ]);
    
    // Tell LocationSelect we're ready to listen for franchise changes
    locationSelectReady.value = true;
    log('OrcamentosForm', 'loadForEdit', 'LocationSelect ready signal sent');
    
    log('OrcamentosForm', 'loadForEdit', 'Loaded successfully');
    loadSuccess = true;
  } catch (error) {
    log('OrcamentosForm', 'loadForEdit', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar orçamento'));
  } finally {
    // Clear loading flag to allow watchers to work normally
    isLoadingData.value = false;
    log('OrcamentosForm', 'loadForEdit', 'Loading flag cleared, watchers now active');
    // Emit AFTER loading flag is cleared so watchers settle first
    if (loadSuccess) {
      eventBus.emit('orcamento.form.ready', { mode: 'edit', id: orcamentoId });
    }
  }
};

const loadForClient = async (clienteId: Id): Promise<void> => {
  log('OrcamentosForm', 'loadForClient', 'Loading client for new budget', clienteId);

  try {
    resetForm();

    // Fetch customer data from backend
    const cliente = await fetchCliente(Number(clienteId)) as Partial<ClienteData> & { cliente_id: Id };
    
    log('OrcamentosForm', 'loadForClient', 'Customer data loaded', cliente);
    
    // Populate orcamento with customer data
    orcamentoData.value.orcamento_cliente_id = clienteId;
    orcamentoData.value.orcamento_franquia_id = cliente.cliente_franquia_id ?? 0;
    orcamentoData.value.orcamento_location_id = cliente.cliente_location_id ?? '';
    orcamentoData.value.orcamento_location_type = cliente.cliente_location_type ?? '';

    // Populate customer fields in the form
    clienteData.value.cliente_id = cliente.cliente_id;
    clienteData.value.cliente_nome = cliente.cliente_nome ?? '';
    clienteData.value.cliente_cpf = cliente.cliente_cpf ?? '';
    clienteData.value.cliente_document_type = cliente.cliente_document_type || selectedDocumentType.value;
    clienteData.value.cliente_ddi = cliente.cliente_ddi || '+55';
    clienteData.value.cliente_ddd = cliente.cliente_ddd ?? '';
    clienteData.value.cliente_telefone = cliente.cliente_telefone ?? '';
    clienteData.value.cliente_cep = cliente.cliente_cep ?? '';
    clienteData.value.cliente_rua = cliente.cliente_rua ?? '';
    clienteData.value.cliente_numero = cliente.cliente_numero ?? '';
    clienteData.value.cliente_bairro = cliente.cliente_bairro ?? '';
    clienteData.value.cliente_complemento = cliente.cliente_complemento ?? '';
    clienteData.value.cliente_franquia_id = cliente.cliente_franquia_id ?? 0;
    clienteData.value.cliente_location_id = cliente.cliente_location_id ?? 0;
    clienteData.value.cliente_location_type = cliente.cliente_location_type ?? '';

    // Set phone model for InputPhone component
    log('OrcamentosForm', 'loadForClient', 'Setting phoneModel from cliente data:', {
      cliente_telefone: clienteData.value.cliente_telefone,
      cliente_ddi: clienteData.value.cliente_ddi,
      cliente_ddd: clienteData.value.cliente_ddd
    });

    phoneModel.value = {
      countryCode: clienteData.value.cliente_ddi || '',
      areaCode: clienteData.value.cliente_ddd || '',
      phoneNumber: clienteData.value.cliente_telefone || ''
    };

    log('OrcamentosForm', 'loadForClient', 'phoneModel after setting:', phoneModel.value);

    // Load initial data
    await Promise.all([
      loadOrigens(),
      loadServicos()
    ]);

    // O orçamento nasce com a origem do lead (chamado DS 2026-0076); sem ela,
    // a lista escolhia a primeira opção (Facebook) e a franquia tinha de corrigir.
    const origemDoLead = Number(cliente.cliente_origem_id);
    if (origemDoLead > 0) {
      orcamentoData.value.orcamento_origem = origemDoLead;
    }

    // Tell LocationSelect we're ready to listen for franchise changes
    locationSelectReady.value = true;
    log('OrcamentosForm', 'loadForClient', 'LocationSelect ready signal sent');
    
    log('OrcamentosForm', 'loadForClient', 'Form populated with customer data');
    eventBus.emit('orcamento.form.ready', { mode: 'new', clienteId });
  } catch (error) {
    log('OrcamentosForm', 'loadForClient', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar dados do cliente: ') + message);
  }
};

const saveBudget = async (): Promise<void> => {
  log('OrcamentosForm', 'saveBudget', 'Saving budget');
  
  if (!canSave.value) {
    log('OrcamentosForm', 'saveBudget', 'Validation failed');
    emit('validation-error');
    return;
  }
  
  enviandoDados.value = true;
  
  try {
    // Prepare data
    // Helper: convert form Id (`number | string`) to a positive number or undefined.
    // Returns undefined for empty string, 0, and non-numeric strings — matching the
    // current `|| null` semantics where falsy values were treated as "no value".
    const toOptionalId = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    };

    // Helper: same as above but for required ids (franquia, orcamento_id, etc.).
    // canSave guarantees orcamento_franquia_id > 0 before this code runs.
    const toRequiredId = (v: unknown): number => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };

    const baseOrcamentoFields = {
      orcamento_user_id: orcamentoData.value.orcamento_user_id || (permissionsState.user?.sub ?? ''),
      orcamento_location_id: toOptionalId(orcamentoData.value.orcamento_location_id),
      orcamento_location_type: orcamentoData.value.orcamento_location_type,
      orcamento_valor: orcamentoData.value.orcamento_valor,
      orcamento_origem: toOptionalId(orcamentoData.value.orcamento_origem),
      orcamento_data: orcamentoData.value.orcamento_data,
      orcamento_status: orcamentoData.value.orcamento_status,
      orcamento_observacao: orcamentoData.value.orcamento_observacao,
      orcamento_cliente_id: toOptionalId(orcamentoData.value.orcamento_cliente_id),
    };

    const orcamentoSalvar: OrcamentoDataInputRow = {
      ...baseOrcamentoFields,
      orcamento_franquia_id: toRequiredId(orcamentoData.value.orcamento_franquia_id),
    };

    const orcamentoUpdate: OrcamentoUpdateDataInputRow = {
      ...baseOrcamentoFields,
      orcamento_id: toRequiredId(orcamentoData.value.orcamento_id),
      orcamento_franquia_id: toOptionalId(orcamentoData.value.orcamento_franquia_id),
    };

    // Preserve the 3-level fallback semantics: cliente.cliente_franquia_id →
    // orcamento.orcamento_franquia_id → undefined.
    const clienteFranquiaId =
      toOptionalId(clienteData.value.cliente_franquia_id) ??
      toOptionalId(orcamentoData.value.orcamento_franquia_id);

    const baseClienteFields = {
      cliente_cpf: clienteData.value.cliente_cpf,
      cliente_document_type: clienteData.value.cliente_document_type,
      cliente_ddi: clienteData.value.cliente_ddi,
      cliente_ddd: clienteData.value.cliente_ddd,
      cliente_rua: clienteData.value.cliente_rua,
      cliente_numero: clienteData.value.cliente_numero,
      cliente_complemento: clienteData.value.cliente_complemento,
      cliente_bairro: clienteData.value.cliente_bairro,
      cliente_cep: clienteData.value.cliente_cep,
      cliente_location_id: toOptionalId(clienteData.value.cliente_location_id),
      cliente_location_type: clienteData.value.cliente_location_type,
      cliente_franquia_id: clienteFranquiaId,
    };

    const clienteSalvar: OrcamentoClienteInputRow = {
      ...baseClienteFields,
      cliente_nome: clienteData.value.cliente_nome,
      cliente_telefone: clienteData.value.cliente_telefone,
      cliente_id: toOptionalId(clienteData.value.cliente_id),
    };

    const clienteUpdate: OrcamentoUpdateClienteInputRow = {
      ...baseClienteFields,
      cliente_id: toRequiredId(clienteData.value.cliente_id),
      cliente_nome: clienteData.value.cliente_nome || undefined,
      cliente_telefone: clienteData.value.cliente_telefone || undefined,
    };

    const Servicos: OrcamentoServicoInputRow[] = servicos.value.map((s) => ({
      servico_id: toRequiredId(s.servico_id),
      valor: Number(s.valor),
      qtd: Number(s.qtd),
      categoria_id: toOptionalId(s.categoria_id),
      categoria_nome: s.categoria_nome || undefined,
    }));

    // Save
    let result: SaveResult;
    if (orcamentoData.value.orcamento_id > 0) {
      const updateData: Orcamentos_UpdateOrcamentoInput = {
        Cliente: clienteUpdate,
        Orcamento: orcamentoUpdate,
        Servicos,
      };
      result = await updateOrcamento(updateData) as SaveResult;
    } else {
      const createData: Orcamentos_SalvarOrcamentoInput = {
        Cliente: clienteSalvar,
        Orcamento: orcamentoSalvar,
        Servicos,
      };
      result = await createOrcamento(createData);
    }

    log('OrcamentosForm', 'saveBudget', 'Saved successfully', result);

    // Show success message
    // Toast é emitido pelo OrcamentosModal.handleSaveSuccess (consumer do save-success).
    // Não emitir aqui — evita addMissingTranslation concorrente e race no
    // translations-service (duas POSTs para o mesmo shortcode → MySQL 1062).
    // eventBus.emit('globalMessage.success', ...) removido — ver fix #4.2.

    // Emit success
    emit('save-success', result);

    // Only refresh filtered listings when the saved record belongs to the active period.
    if (!showDateAlert.value) {
      eventBus.emit('orcamento.saved', result);
    }

  } catch (error) {
    log('OrcamentosForm', 'saveBudget', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', message || t('Erro ao salvar orçamento'));
  } finally {
    enviandoDados.value = false;
  }
};

const resetForm = (): void => {
  log('OrcamentosForm', 'resetForm', 'Resetting form');
  
  orcamentoData.value = {
    orcamento_id: 0,
    orcamento_franquia_id: 0,
    orcamento_user_id: '',
    orcamento_location_id: '',
    orcamento_location_type: '',
    orcamento_valor: 0,
    orcamento_origem: 0,
    orcamento_data: '',
    orcamento_status: 'Pendente',
    orcamento_observacao: '',
    orcamento_cliente_id: 0
  };
  
  clienteData.value = {
    cliente_id: 0,
    cliente_nome: '',
    cliente_cpf: '',
    cliente_document_type: '',
    cliente_ddi: '',
    cliente_ddd: '',
    cliente_telefone: '',
    cliente_rua: '',
    cliente_numero: '',
    cliente_complemento: '',
    cliente_bairro: '',
    cliente_cep: '',
    cliente_location_id: 0,
    cliente_location_type: '',
    cliente_franquia_id: 0
  };
  
  log('OrcamentosForm', 'resetOrcamento', 'Resetting phoneModel to empty');
  phoneModel.value = {
    phoneNumber: '',
    countryCode: '',
    areaCode: ''
  };
  
  servicos.value = [];
  enviandoDados.value = false;
  locationSelectReady.value = false;
};



const loadAtendentes = async (franquiaId: Id): Promise<void> => {
  log('OrcamentosForm', 'loadAtendentes', 'Loading atendentes for franchise', franquiaId);

  try {
    if (franquiaId) {
      atendentes.value = permissionsService.getAtendentesSelectArrayByFranquiaID(Number(franquiaId)) as SelectOption[];
      log('OrcamentosForm', 'loadAtendentes', 'Loaded atendentes:', atendentes.value);
    } else {
      atendentes.value = [];
      log('OrcamentosForm', 'loadAtendentes', 'No franchise ID provided, clearing atendentes');
    }
  } catch (error) {
    log('OrcamentosForm', 'loadAtendentes', 'Error loading atendentes:', error);
    atendentes.value = [];
  }
};

const selectPreferredAtendenteId = (): string => {
  const currentUserId = permissionsState.user?.sub || '';
  const currentSelection = orcamentoData.value.orcamento_user_id || '';

  if (currentSelection && atendentes.value.some((atendente) => atendente.ID === currentSelection)) {
    return currentSelection;
  }

  if (currentUserId && atendentes.value.some((atendente) => atendente.ID === currentUserId)) {
    return currentUserId;
  }

  return '';
};

const loadOrigens = async (): Promise<void> => {
  log('OrcamentosForm', 'loadOrigens', 'Loading origens');

  try {
    origens.value = await fetchOrigens() as SelectOption[];
  } catch (error) {
    log('OrcamentosForm', 'loadOrigens', 'Error', error);
  }
};

const loadServicos = async (): Promise<void> => {
  log('OrcamentosForm', 'loadServicos', 'Loading servicos');

  try {
    const servicosData = await fetchServicos();

    // Group by category
    categorias.value = {};
    for (const servico of servicosData) {
      const catKey = String(servico.servicoscategoria_id);
      if (!categorias.value[catKey]) {
        categorias.value[catKey] = {
          servicoscategoria_id: servico.servicoscategoria_id,
          servicoscategoria_nome: servico.servicoscategoria_nome,
          servicos: []
        };
      }
      categorias.value[catKey].servicos.push(servico);
    }

    log('OrcamentosForm', 'loadServicos', 'Loaded categories', categorias.value);
  } catch (error) {
    log('OrcamentosForm', 'loadServicos', 'Error', error);
  }
};

const handleFranquiaChange = async (): Promise<void> => {
  log('OrcamentosForm', 'handleFranquiaChange', 'Franquia changed', orcamentoData.value.orcamento_franquia_id);

  clienteData.value.cliente_franquia_id = orcamentoData.value.orcamento_franquia_id || 0;
  
  // During data loading (loadForEdit), do not reset location; preserve preexisting values
  if (isLoadingData.value) {
    log('OrcamentosForm', 'handleFranquiaChange', 'Skipping location reset due to loading flag');
  } else {
    // User-initiated franchise change: clear location fields to force re-selection
    orcamentoData.value.orcamento_location_id = '';
    orcamentoData.value.orcamento_location_type = '';
  }
  
  await loadAtendentes(orcamentoData.value.orcamento_franquia_id);
  
  // Preserve the current selection when still valid; otherwise prefer the logged-in user.
  if (atendentes.value.length > 0) {
    orcamentoData.value.orcamento_user_id = selectPreferredAtendenteId();
    log('OrcamentosForm', 'handleFranquiaChange', 'Selected atendente', orcamentoData.value.orcamento_user_id);
  } else {
    // Clear atendente if no atendentes available for this franchise
    orcamentoData.value.orcamento_user_id = '';
    log('OrcamentosForm', 'handleFranquiaChange', 'No atendentes available, cleared selection');
  }
};

// Removed: handleLocationChange. LocationSelect updates orcamentoData location fields directly.

const handleCepLookup = async (): Promise<void> => {
  log('OrcamentosForm', 'handleCepLookup', 'Looking up CEP', clienteData.value.cliente_cep);

  if (!isBrazil.value) return;

  const cep = clienteData.value.cliente_cep?.replace(/\D/g, '');
  if (!cep || cep.length !== 8) return;

  try {
    const result = await consultaCep(cep) as CepResult;

    clienteData.value.cliente_rua = result.logradouro || '';
    clienteData.value.cliente_bairro = result.bairro || '';

    // Try to find matching location
    if (result.localidade) {
      log('OrcamentosForm', 'handleCepLookup', 'Found localidade:', result.localidade, 'but LocationSelect handles location matching');
    }

    log('OrcamentosForm', 'handleCepLookup', 'CEP lookup successful', result);
  } catch (error) {
    log('OrcamentosForm', 'handleCepLookup', 'Error', error);
    // Don't show error to user, CEP lookup is optional
  }
};

const openAddServicoWizard = (): void => {
  log('OrcamentosForm', 'openAddServicoWizard', 'Opening add service wizard via eventBus');
  eventBus.emit('modal.orcamento-add-servico.open', {
    categorias: categorias.value
  });
};

const handleServiceAddedFromWizard = (servicoData?: ServicoItem): void => {
  log('OrcamentosForm', 'handleServiceAddedFromWizard', 'Service added from wizard', servicoData);

  if (!servicoData) return;

  // Add the service to the list
  servicos.value.push(servicoData);

  // Recalculate total
  calculateTotal();

  // Show success message
  eventBus.emit('globalMessage.success', t('Serviço adicionado com sucesso'));
};

const removerServico = (index: number): void => {
  log('OrcamentosForm', 'removerServico', 'Removing service', index);
  servicos.value.splice(index, 1);
  calculateTotal();
};

const getServicoName = (itemServico: ServicoItem): string => {
  const categoria = categorias.value[String(itemServico.categoria_id)];
  if (!categoria) return t('Serviço não encontrado');

  const servico = categoria.servicos.find(s => s.servico_id === itemServico.servico_id);
  return servico ? servico.servico_nome : t('Serviço não encontrado');
};

const calculateTotal = (): void => {
  orcamentoData.value.orcamento_valor = totalValue.value;
};

// AI Completion handlers
const getJsonSchemaForServices = (): unknown[] => {
  return [
    {
      categoria_id: "2",
      categoria_nome: "Limpeza",
      qtd: 1,
      servico_id: "1",
      valor: "150"
    }
  ];
};

const getExtraInformations = (): unknown => {
  return {
    servicos_categorias: categorias.value,
    instrucoes: [
      "Retornar um array de objetos JSON. Preencher um objeto referente a cada serviço dentro do array JSON enviado",
      "se não souber alguma informação, deixe em branco"
    ]
  };
};

const handleAiSuccess = (result: unknown): void => {
  log('OrcamentosForm', 'handleAiSuccess', 'AI completion success', result);

  if (Array.isArray(result)) {
    servicos.value = result.map((item: Partial<ServicoItem>) => ({
      ...item,
      uuid: `servico_${Date.now()}_${Math.random()}`
    })) as ServicoItem[];

    calculateTotal();
  }
};

const handleAiError = (error: unknown): void => {
  log('OrcamentosForm', 'handleAiError', 'AI completion error', error);
  const message = typeof error === 'string' ? error : (error instanceof Error ? error.message : String(error));
  eventBus.emit('globalMessage.error', t('Erro-ao-inferir-dados') + ': ' + message);
};

// refreshServicos - exposed method for parent to refresh servicos list
const refreshServicos = (): void => {
  log('OrcamentosForm', 'refreshServicos', 'Refreshing servicos');
  loadServicos();
};

// Watch phone model and sync to clienteData using usePhoneFormat
watch(phoneModel, (newValue, oldValue) => {
  // Skip watcher during data loading to prevent overwriting loaded data
  if (isLoadingData.value) {
    log('OrcamentosForm', 'watch:phoneModel', 'Skipping phoneModel watcher during data loading');
    return;
  }

  log('OrcamentosForm', 'watch:phoneModel', 'phoneModel changed:', { oldValue, newValue });

  const fields = phoneToFields(newValue);
  clienteData.value.cliente_ddi = fields.ddi;
  clienteData.value.cliente_ddd = fields.ddd;
  clienteData.value.cliente_telefone = fields.telefone;

  log('OrcamentosForm', 'watch:phoneModel', 'Updated clienteData phone fields');
}, { deep: true });

// Watch CEP for auto-lookup
watch(() => clienteData.value.cliente_cep, (newValue, oldValue) => {
  if (newValue && newValue !== oldValue) {
    const cleanCep = newValue.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      handleCepLookup();
    }
  }
});

// Watch franchise ID for atendentes reload
watch(() => orcamentoData.value.orcamento_franquia_id, (newValue, oldValue) => {
  if (newValue && newValue !== oldValue && !isLoadingData.value) {
    log('OrcamentosForm', 'watch:orcamento_franquia_id', 'Franchise changed from', oldValue, 'to', newValue, '(not loading)');
    handleFranquiaChange();
  } else if (isLoadingData.value) {
    log('OrcamentosForm', 'watch:orcamento_franquia_id', 'Franchise changed during loading, skipping auto-selection');
  }
});

// Lifecycle hooks for centralized ModalManager pattern
onMounted(() => {
  log('OrcamentosForm', 'onMounted', 'Registering eventBus listeners');
  eventBus.on('orcamento-add-servico.service-added', handleServiceAddedFromWizard);
});

onBeforeUnmount(() => {
  log('OrcamentosForm', 'onBeforeUnmount', 'Cleaning up eventBus listeners');
  eventBus.off('orcamento-add-servico.service-added', handleServiceAddedFromWizard);
});

// Expose methods
defineExpose({
  initializeForNew,
  loadForEdit,
  loadForClient,
  saveBudget,
  resetForm,
  refreshServicos,
  canSave,
  calculateTotal,
  orcamentoData,
  faltaParaAgendar
});
</script>

<style scoped>
.orcamentos-form {
  padding: 20px 0;
}

/* Action buttons container */
.action-buttons-right {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-bottom: 20px;
}

/* PDF download button */
.pdf-download-button {
  width: 100%;
  margin-top: 20px;
  margin-bottom: 20px;
}

/* Cancelled budget message */
.cancelled-budget-message h3 {
  margin-top: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Address row flexbox */
.address-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 15px;
}

.address-cep {
  flex: 1 1 calc(33.333% - 10px);
  min-width: 120px;
}

.address-street {
  flex: 1 1 calc(33.333% - 10px);
  min-width: 160px;
}

.address-street-wide {
  flex: 2 1 calc(66.666% - 10px);
  min-width: 200px;
}

.address-number {
  flex: 1 1 calc(33.333% - 10px);
  min-width: 120px;
}

@media (max-width: 768px) {
  .address-row {
    flex-direction: column;
  }

  .address-cep,
  .address-street,
  .address-street-wide,
  .address-number {
    flex: 1 1 100%;
    min-width: 100%;
  }
}

/* Add service container */
.add-service-container {
  margin-bottom: 20px;
  position: relative;
}

/* Total value */
.total-value {
  text-align: right;
  margin-top: 20px;
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
}

/* Services List Container */
.services-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}

/* Service Item */
.service-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #ffffff;
  transition: all 0.2s ease;
}

.service-item:hover {
  border-color: #f59e0b;
  background-color: #fffbeb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* Service Badge */
.service-badge {
  flex-shrink: 0;
}

/* Service Info */
.service-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.service-categoria {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6366f1;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.service-name {
  font-weight: 500;
  font-size: 0.9375rem;
  color: #1f2937;
  line-height: 1.3;
}

.service-details {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.8125rem;
  color: #6b7280;
}

.service-qty {
  font-weight: 500;
}


/* Service Total */
.service-total {
  font-weight: 600;
  font-size: 1rem;
  color: #059669;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 80px;
  text-align: right;
}

/* Service Remove Button */
.service-remove {
  flex-shrink: 0;
  margin-left: 0.5rem;
}

/* Empty State */
.services-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2.5rem 1.5rem;
  color: #9ca3af;
  border: 1px dashed #d1d5db;
  border-radius: 0.5rem;
  background: #fafafa;
}

.services-empty i {
  font-size: 1.5rem;
}

/* Responsive */
@media (max-width: 640px) {
  .service-item {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .service-badge {
    order: 1;
  }

  .service-info {
    order: 2;
    flex: 1 1 calc(100% - 120px);
  }

  .service-remove {
    order: 3;
  }

  .service-total {
    order: 4;
    width: 100%;
    text-align: right;
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid #e5e7eb;
  }
}

/* Ensure v-select dropdown renders properly in modal */
.orcamentos-form ::v-deep(.vs__dropdown-toggle) {
  min-height: 34px;
}

.orcamentos-form ::v-deep(.vs__search) {
  margin: 4px 0 0;
  padding: 0 7px;
  font-size: 14px;
}

.orcamentos-form ::v-deep(.vs__dropdown-menu) {
  max-height: 250px;
  overflow-y: auto;
  z-index: 1050 !important;
}

.orcamentos-form ::v-deep(.vs__selected) {
  margin: 4px 2px 0;
  padding: 0 4px;
}
</style>
